import { useEffect, useState } from "react";
import { GetEntityAttributes } from "../api/entityService";
import { loadCatalog, setCachedCatalog } from "../builder/entityCatalogCache";
import { EntityAttribute } from "../builder/entityAttribute.types";
import { EntityFieldCatalogEntry } from "../builder/entityFieldCatalog.types";

type Dispatch = (action: unknown) => void;

/** Reads an entity's metadata once per session. A failed read leaves the search with no fields. */
export function loadSearchCatalog(
  globalDispatch: Dispatch,
  entityName: string
): Promise<EntityFieldCatalogEntry[]> {
  if (!entityName) {
    return Promise.resolve([]);
  }

  const name = entityName.toLowerCase();

  return loadCatalog(name, async (entity) => {
    const response = (await GetEntityAttributes(globalDispatch, entity)) as {
      data?: EntityAttribute[];
    };

    return Array.isArray(response?.data) ? response.data : [];
  }).catch(() => {
    setCachedCatalog(name, []);

    return [];
  });
}

/** Loads the given entities' metadata, and reports whether all of it has arrived. */
export function useSearchCatalogs(globalDispatch: Dispatch, entityNames: string[]) {
  const [loadedKey, setLoadedKey] = useState("");

  // A stable dependency, so a caller building the array inline each render doesn't reload.
  const key = (entityNames ?? []).filter(Boolean).join(",");

  useEffect(() => {
    if (key === "") {
      return undefined;
    }

    let cancelled = false;

    Promise.all(
      key.split(",").map((entityName) => loadSearchCatalog(globalDispatch, entityName))
    ).then(() => {
      if (!cancelled) {
        setLoadedKey(key);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [globalDispatch, key]);

  return loadedKey === key;
}
